import { comparer } from "mobx";

/**
 * The keys of an update body whose value differs from the record it updates:
 * the `fields` prop of a `<resource>_updated` event (NG-2054). Forms often send
 * every field on every save, so the body's keys alone would name them all. A
 * record that isn't loaded yet counts every key the body sends.
 *
 * Its own module rather than part of `analytics.ts`, which tests mock with a
 * `track`-only factory: a store reading this through that mock would throw.
 */
export function changedFields(body: object, before: object | undefined): string[] {
  const next = body as Record<string, unknown>;
  const prev = before as Record<string, unknown> | undefined;
  return Object.keys(next).filter((key) => !prev || !comparer.structural(next[key], prev[key]));
}

/**
 * How many ids a whole-list save adds and removes against the list it replaces:
 * the `count` props of a `<resource>_assigned` / `_unassigned` pair (NG-2054).
 * Pass an empty `before` for a list that was never loaded, which counts only
 * the adds.
 */
export function listChanges(
  before: Iterable<string>,
  after: Iterable<string>,
): { added: number; removed: number } {
  const prior = new Set(before);
  const next = new Set(after);
  let added = 0;
  for (const id of next) if (!prior.has(id)) added++;
  let removed = 0;
  for (const id of prior) if (!next.has(id)) removed++;
  return { added, removed };
}
